import { SxProp, View, useDripsyTheme, styled, Pressable, FlatList, ActivityIndicator } from 'dripsy';
import React, { useContext, useEffect, useState } from 'react';
import { AccountManagementStackRoutes, AccountManagementStackScreenProps } from '@/features/account-management/account.navigator';
import moment from 'moment-timezone';
import { TextTitle } from '@/design-system/components/typography';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniIconButton } from '@/design-system/components/buttons';
import { Row } from '@/design-system/components/layout';
import { LinearGradient } from 'expo-linear-gradient';
import { UniInput } from '@/design-system/components/inputs';
import { TextBody, TextCaption } from '@/design-system/components/typography';
import { useAsyncStorage } from '@/devtools/hooks/useAsyncStorage';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { TransactionStatus } from '@/features/account-management/enum';
import { TransactionStatusFilters } from '@/features/account-management/options';
import { useTranslation } from 'react-i18next';
import { keyGenerator } from '@/utils/keyGenerator';
import { useCardTransaction } from '@/features/account-management/screens/details/hooks/useCard.hook';
import { LayoutContext } from 'contexts/LayoutContext';
import { RootStackRoutes } from 'types';
import { DownloadTransactionReportStackRoutes } from '@/features/account-management/screens/download-transaction-report/type';
import { useNavigation } from '@react-navigation/native';

export interface CardTransactionHistoryProps {}

export const CardTransactionHistoryScreen: React.FC<
  AccountManagementStackScreenProps<AccountManagementStackRoutes.CardTransactionHistory>
> = ({ navigation, route }) => {
  const { account } = route.params;
  const navigationAny = useNavigation<any>();
  const Gradient = styled(LinearGradient, {})({});
  const { t } = useTranslation('account-management');
  const { value: show, set: setShow } = useAsyncStorage<boolean>('showAmount');
  const { theme } = useDripsyTheme();
  const layout = useContext(LayoutContext);
  const onGoBack = () => {
    navigation.goBack();
  };

  const hideShowBalance = (balance: string) => {
    return show ? '******' : balance;
  };
  const [search, setSearch] = useState<string | undefined>(undefined);

  const [filterValues, setFilterValues] = useState<any>();

  const getColor = (status: string) => {
    switch (status) {
      case TransactionStatus.PENDING:
        return '#28ACFF';
      case TransactionStatus.SUCCESS:
        return theme?.colors?.$success as string;
      case TransactionStatus.DECLINED:
        return theme?.colors?.$danger as string;
      default:
        return theme?.colors?.$textSecondary as string;
    }
  };

  const detailNavigation = useNavigation();

  const {
    transactions,
    isLoadingCardTransactions,
    reloadCardTransactions,
    nextToken,
    preToken,
    next,
    prev,
    refreshCardTransactions,
    clearCardTransactionCurrentPageToken
  } = useCardTransaction({
    cardAccountId: account,
    dateRangeFrom: filterValues?.fromDate,
    dateRangeTo: filterValues?.toDate,
    search: search
  });

  const [transactionsData, setTransactionsData] = useState([]);
  useEffect(() => {
    let transactionsFlag = transactionsData;
    transactions.forEach(
      (transaction) =>
        (transactionsFlag.length &&
          !transactionsFlag.filter((tf) => tf?.id == transaction?.id).length &&
          transactionsFlag.push(transaction)) ||
        (!transactionsFlag.length && transactionsFlag.push(transaction))
    );
    setTransactionsData(transactionsFlag);
  }, [transactions, transactionsData]);

  const flatlistHight = layout.dimensions.height - 160;

  return (
    <View sx={styles.contentContainer}>
      <Gradient colors={['rgba(49, 75, 108, 0.8)', 'rgba(39, 70, 115, 0.8)', '#3C6291', '#1E304B']}>
        <Row sx={{ justifyContent: 'space-between', alignItems: 'center', px: '$4', mt: '$5', mb: '$4' }}>
          <UniIconButton
            icon="arrowLeft"
            options={{ iconStyles: { color: theme?.colors?.$white as string } }}
            onPress={onGoBack}></UniIconButton>
          <View sx={{ textAlign: 'center' }}>
            <TextTitle bold sx={{ color: theme?.colors?.$white as string }}>
              {t('card-account-management::account-page.title--transaction-history')}
            </TextTitle>
          </View>
          <View sx={{ flexDirection: 'row', gap: '$1' }}>
            <UniIconButton
              icon="historyCalendar"
              options={{ iconStyles: { color: theme?.colors?.$white as string } }}
              onPress={() => {
                navigation.navigate(AccountManagementStackRoutes.BottomSheetDatePicker, {
                  value: {
                    from: filterValues?.fromDate,
                    to: filterValues?.toDate
                  },
                  onChange: (value) => {
                    setTransactionsData([]);
                    clearCardTransactionCurrentPageToken();
                    setFilterValues({
                      ...filterValues,
                      fromDate: value.from?.toDate(),
                      toDate: value.to?.toDate()
                    });
                  }
                });
              }}></UniIconButton>
            <UniIconButton
              icon="exportsymbol"
              options={{ iconStyles: { color: theme?.colors?.$white as string, width: 24, height: 24 } }}
              onPress={() => {
                navigationAny.navigate(RootStackRoutes.AccountManagement, {
                  screen: AccountManagementStackRoutes.DownloadTransactionReport,
                  params: {
                    screen: DownloadTransactionReportStackRoutes.DownloadCardAccountTransactionReport,
                    params: { creditLineId: account }
                  }
                });
              }}></UniIconButton>
          </View>
        </Row>
      </Gradient>
      <View sx={styles.card}>
        <View sx={styles.table}>
          <View sx={styles.operations}>
            <View sx={styles.filters}>
              <UniIconButton
                sx={{
                  borderWidth: 1,
                  borderColor: theme?.colors?.$borderButton as string,
                  borderRadius: 5,
                  width: 36,
                  justifyContent: 'center'
                }}
                icon="refresh"
                onPress={() => {
                  setTransactionsData([]);
                  setFilterValues({ fromDate: undefined, toDate: undefined });
                  setSearch(undefined);
                  refreshCardTransactions();
                }}
              />
              <UniInput
                value={search}
                sx={styles.filter_search}
                size="default"
                prefixIcon="search"
                placeholder={t('card-account-management::card-details.text--search-by-desc-ref-id')}
                onValueChange={(value) => {
                  setTransactionsData([]);
                  clearCardTransactionCurrentPageToken();
                  setSearch(value);
                }}
              />
            </View>
          </View>
          <FlatList
            data={transactionsData}
            renderItem={({ item }) => {
              return (
                <Pressable onPress={() => {}} sx={{ px: '$4' }}>
                  <View sx={{ my: 6, gap: 20, borderRadius: 10, boxShadow: '2px 4px 20px rgba(0, 0, 0, 0.07)' }}>
                    <View sx={{ margin: '$4' }}>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between' }}>
                        <View sx={{ justifyContent: 'center', gap: '$1' }}>
                          <TextBody semiBold sx={{ color: '$textPrimary' }}>
                            {item?.description}
                          </TextBody>
                          {(item?.cardEmbossedName || item?.cardLast4) && (
                            <TextCaption sx={{ color: '$textSecondary' }}>
                              {item?.cardEmbossedName} {item?.cardLast4 ? `**** ${item?.cardLast4}` : ''}
                            </TextCaption>
                          )}
                        </View>
                        <View sx={{ width: '$10', height: '$10', justifyContent: 'center', zIndex: 1 }}>
                          {!!item.xmetadata && (
                            <UniIconButton
                              icon="info"
                              onPress={(e) => {
                                detailNavigation.navigate(RootStackRoutes.AccountManagement, {
                                  screen: AccountManagementStackRoutes.Xmetadata,
                                  params: { xmetadata: item.xmetadata }
                                });
                              }}
                            />
                          )}
                        </View>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$3' }}>
                        <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--amount')}</TextBody>
                        <TextBody
                          semiBold
                          sx={{
                            color: (item?.status == 'pending' ? theme?.colors?.$navbarTextInactive : theme?.colors?.$secondary) as string
                          }}>
                          {hideShowBalance(
                            formatAmountByDecimal({
                              value: item?.amount,
                              suffix: `${item?.currency} ${item?.entryType == 'CREDIT' ? '[CR]' : ''}`,
                              decimals: 2
                            })
                          )}
                        </TextBody>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$2' }}>
                        <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::card-details.text--post-date')}</TextBody>
                        <TextBody sx={{ ml: '$2', color: '$textPrimary' }}>
                          {item?.postedAt ? moment(item?.postedAt).format('YYYY-MM-DD') : '-'}
                        </TextBody>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$2' }}>
                        <TextBody sx={{ color: '$textSecondary' }}>
                          {t('card-account-management::card-details.text--transaction-date')}
                        </TextBody>
                        <TextBody sx={{ color: '$textPrimary' }}>
                          {item?.createdAt ? moment(item?.createdAt).format('YYYY-MM-DD') : '-'}
                        </TextBody>
                      </View>
                      <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', mt: '$2' }}>
                        <TextBody sx={{ color: '$textSecondary' }}>{t('card-account-management::account-page.text--status')}</TextBody>
                        <View sx={{ flexDirection: 'row', alignItems: 'center' }}>
                          <Icon name="dotsymbol" color={getColor(item?.status)} />
                          <TextCaption sx={{ color: getColor(item?.status) }}>
                            {TransactionStatusFilters(t).find((el) => el.value === item?.status)?.label}
                          </TextCaption>
                        </View>
                      </View>
                    </View>
                  </View>
                </Pressable>
              );
            }}
            scrollEnabled={true}
            showsVerticalScrollIndicator={false}
            sx={{ flex: 1, maxHeight: flatlistHight }}
            keyExtractor={(_, index) => keyGenerator(`card-transaction-${index}`)}
            onEndReachedThreshold={0.05}
            onEndReached={() => {
              if (!isLoadingCardTransactions) {
                if (nextToken) next();
              }
            }}
          />
          {isLoadingCardTransactions && (
            <View sx={{ padding: '$5' }}>
              <ActivityIndicator size="large" color={theme.colors?.$disable || '#F0F0F0'} />
            </View>
          )}
        </View>
        {/* <View sx={{ alignItems: 'flex-end', px: '$4' }}>
          <TokenPagination prevPageToken={preToken} nextPageToken={nextToken} onNextPage={next} onPrevPage={prev} />
        </View> */}
      </View>
    </View>
  );
};

enum StyleName {
  contentContainer = 'contentContainer',
  card = 'card',
  table = 'table',
  operations = 'operations',
  filters = 'filters',
  filter_search = 'filter_search'
}

const styles: Record<StyleName, SxProp> = {
  contentContainer: {
    gridTemplateColumns: '1fr',
    gridTemplateAreas: `
      "overview assets"
      "transaction assets"
      "transaction assets"
      `,
    gridTemplateRows: 'repeat(3, auto)',
    height: '100%',
    backgroundColor: '#FFFFFF'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$4',
    borderRadius: '$radius0',
    boxShadow: 'none',
    paddingY: '$5',
    backgroundColor: '$white'
  },
  table: {
    flexDirection: 'column',
    gap: '$4'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$3',
    width: '100%'
  },
  filter_search: {
    width: '100%'
  }
};
